import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/system';
import Cookies from 'js-cookie';
import { changePassword } from '../../apis/ChangePwd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
    height: 45,
    alignItems: 'center',   

    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },
    '&.Mui-focused fieldset': {
      border: '0.6px solid #5965A6',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#5965A6',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#5965A6',
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: '#d32f2f',
  },
}));


const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [formValid, setFormValid] = useState(false);
  const [username, setUsername] = useState('');

  // visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const cookieUsername = Cookies.get('username');
    if (cookieUsername) {
      setUsername(cookieUsername);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setPasswordMatch(value === newPassword);
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    const matches = confirmPassword ? value === confirmPassword : true;
    setPasswordMatch(matches);
  };

  const handlePasswordChange = async () => {
    if (!passwordMatch) {
      setError("Passwords don't match");
      return;
    }

    if (!username) {
      setError('Username not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await changePassword(currentPassword, newPassword, username);
      Cookies.remove('requiresPasswordChange');

      Cookies.remove('authToken');
      Cookies.remove('username');
      Cookies.remove('userId');
      Cookies.remove('Org_id');
      Cookies.remove('Role_id');
      navigate('/login');
    } catch (error) {
      setError(error.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const isValid =
      currentPassword.trim() !== '' &&
      newPassword.trim() !== '' &&
      confirmPassword.trim() !== '' &&
      passwordMatch;
    setFormValid(isValid);
  }, [currentPassword, newPassword, confirmPassword, passwordMatch]);

  const renderVisibilityIcon = (visible, setVisible) => (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setVisible((prev) => !prev)}
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
        size="small"
        sx={{color: '#5965A6',
          '& .MuiSvgIcon-root': {
          fontSize: 20,         
        },
        }}
      >
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#F4F7FA',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 500,
          padding: 4,
          borderRadius: 2,
          backgroundColor: '#fff',
          boxShadow: 3,
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{ fontWeight: 700, marginBottom: 3 }}
        >
          Change Password
        </Typography>

        {error && (
          <Snackbar
            open={true}
            autoHideDuration={6000}
            onClose={() => setError('')}
          >
            <Alert
              onClose={() => setError('')}
              severity="error"
              sx={{ width: '100%' }}
            >
              {error}
            </Alert>
          </Snackbar>
        )}

        <Box component="form" noValidate onSubmit={(e) => e.preventDefault()}>
          {/* Current Password */}
          <StyledTextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ marginBottom: 3 }}
            InputProps={{
              endAdornment: renderVisibilityIcon(
                showCurrentPassword,
                setShowCurrentPassword,
              ),
            }}
          />

          {/* New Password */}
          <StyledTextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={handleNewPasswordChange}
            sx={{ marginBottom: 3 }}
            InputProps={{
              endAdornment: renderVisibilityIcon(
                showNewPassword,
                setShowNewPassword,
              ),
            }}
          />

          {/* Confirm New Password */}
          <StyledTextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            sx={{ marginBottom: 3 }}
            error={!passwordMatch}
            helperText={!passwordMatch ? "Passwords don't match" : ''}
            InputProps={{
              endAdornment: renderVisibilityIcon(
                showConfirmPassword,
                setShowConfirmPassword,
              ),
            }}
          />

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handlePasswordChange}
            disabled={loading || !formValid}
            sx={{
              padding: '12px',
              fontSize: '0.975rem',
              fontWeight: 500,
              backgroundColor: '#3E9663',
              '&:hover': { backgroundColor: '#357A52' },
            }}
          >
            {loading ? 'Changing...' : 'Confirm Change'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChangePasswordPage;
